import { useEffect, useState } from "react";
import { IClientes } from "../../interfaces/IClientes";
import { Modal } from 'react-bootstrap-v5';
import clsx from "clsx";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import ClientesService from "../../services/ClientesService";
import { LISTA_ESTADOS, MASCARA_CEP, MASCARA_DATA, MASCARA_TELEFONE } from "../../utils/Constants";
import MaskedInput from "react-text-mask";
import ClientesPlanoTab from "./ClientesPlanoTab";
import ClientesVolumesContratadosTab from "./ClientesVolumesContratadosTab";
import ClientesAuditoriaTab from "./ClientesAuditoriaTab";
import ClientesWorkflowTab from "./ClientesWorkflowTab";
import ClientesMaestroTab from "./ClientesMaestroTab";

const ABAS = [
  { id: '1', label: 'Dados do Cliente' },
  { id: '2', label: 'Planos' },
  { id: '3', label: 'Volumes Contratados' },
  { id: '4', label: 'Auditoria' },
  { id: '5', label: 'Workflow' },
  { id: '6', label: 'Maestro Contábil' },
];

const ClientesModal = ({ toggleModal, show, cliente, title }: { toggleModal: (recarregarInformacoes?: boolean) => void, show: boolean, cliente: IClientes, title: string }) => {

  const [abaSelecionada, setAbaSelecionada] = useState<string>('1');

  const clienteSchema = Yup.object().shape({
    cnpj: Yup.string().min(13, 'Mínimo 13 caracteres').max(18, 'Máximo 18 caracteres').required('CNPJ/CPF é obrigatório'),
    dataAberturaString: Yup.string().min(10, 'Mínimo 10 caracteres').max(10, 'Máximo 10 caracteres').required('Data de Abertura é obrigatório'),
    razaoSocial: Yup.string().max(350, 'Máximo 350 caracteres').required('Razão Social é obrigatório'),
    nomeFantasia: Yup.string().max(250, 'Máximo 250 caracteres').required('Nome Fantasia é obrigatório'),
    porte: Yup.string().max(100, 'Máximo 100 caracteres').required('Porte é obrigatório'),
    atividadePrincipal: Yup.string().max(250, 'Máximo 250 caracteres').required('Atividade Principal é obrigatório'),
    atividadeSecundaria: Yup.string().max(250, 'Máximo 250 caracteres'),
    cep: Yup.string().required('CEP é obrigatório'),
    endereco: Yup.string().max(250, 'Máximo 250 caracteres').required('Endereço é obrigatório'),
    numero: Yup.string().max(10, 'Máximo 10 caracteres').required('Número é obrigatório'),
    complemento: Yup.string().max(150, 'Máximo 150 caracteres'),
    bairro: Yup.string().max(150, 'Máximo 150 caracteres').required('Bairro é obrigatório'),
    cidade: Yup.string().max(150, 'Máximo 150 caracteres').required('Cidade é obrigatório'),
    estado: Yup.string().min(2, 'Mínimo 2 caracteres').max(2, 'Máximo 2 caracteres').required('Estado é obrigatório'),
    email: Yup.string().email().max(250, 'Máximo 250 caracteres').required('E-mail é obrigatório'),
    telefone: Yup.string().min(10, 'Mínimo 10 caracteres').max(20, 'Máximo 20 caracteres').required('Telefone é obrigatório'),
    telefoneAdicional: Yup.string().min(10, 'Mínimo 10 caracteres').max(20, 'Máximo 20 caracteres'),
    situacaoCadastral: Yup.string().max(250, 'Máximo 250 caracteres').required('Situação Cadastral é obrigatório'),
    dataSituacaoCadastralString: Yup.string().min(10, 'Mínimo 10 caracteres').max(10, 'Máximo 10 caracteres').required('Data de Situação Cadastral é obrigatório'),
    motivoSituacaoCadastral: Yup.string().max(500, 'Máximo 500 caracteres'),
    situacaoEspecial: Yup.string().max(250, 'Máximo 250 caracteres'),
    dataSituacaoEspecialString: Yup.string().min(10, 'Mínimo 10 caracteres').max(10, 'Máximo 10 caracteres'),
    clienteDesdeString: Yup.string().min(10, 'Mínimo 10 caracteres').max(10, 'Máximo 10 caracteres').required('Cliente Desde é obrigatório')
  });

  const formInitialValues: IClientes = {
    id: 0,
    cnpj: '',
    dataAberturaString: '',
    razaoSocial: '',
    nomeFantasia: '',
    porte: '',
    atividadePrincipal: '',
    atividadeSecundaria: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    email: '',
    telefone: '',
    telefoneAdicional: '',
    situacaoCadastral: '',
    dataSituacaoCadastralString: '',
    motivoSituacaoCadastral: '',
    situacaoEspecial: '',
    dataSituacaoEspecialString: '',
    clienteDesdeString: ''
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema: clienteSchema,
    onSubmit: async (values, { setSubmitting }) => {
      function funcaoSucesso() {
        setSubmitting(false);
        toggleModal(true);
      }

      if (values.dataAberturaString) {
        const p = values.dataAberturaString.split('/');
        values.dataAbertura = new Date(`${p[2]}-${p[1]}-${p[0]} 00:00`);
      }

      if (values.dataSituacaoCadastralString) {
        const p = values.dataSituacaoCadastralString.split('/');
        values.dataSituacaoCadastral = new Date(`${p[2]}-${p[1]}-${p[0]} 00:00`);
      }

      if (values.dataSituacaoEspecialString) {
        const p = values.dataSituacaoEspecialString.split('/');
        values.dataSituacaoEspecial = new Date(`${p[2]}-${p[1]}-${p[0]} 00:00`);
      }

      if (values.clienteDesdeString) {
        const p = values.clienteDesdeString.split('/');
        values.clienteDesde = new Date(`${p[2]}-${p[1]}-${p[0]} 00:00`);
      }

      if (values.id === 0) {
        await ClientesService.criar(values, funcaoSucesso, 'Cliente foi adicionado com sucesso');
      } else {
        await ClientesService.atualizar(values, funcaoSucesso, 'Cliente foi editado com sucesso');
      }
    }
  });

  function formatarData(data?: Date | string): string {
    if (!data) return '';
    const d = new Date(data);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  useEffect(() => {
    formik.resetForm();
    setAbaSelecionada('1');

    if (cliente.id > 0) {
      formik.setValues({
        ...cliente,
        dataAberturaString: formatarData(cliente.dataAbertura),
        dataSituacaoCadastralString: formatarData(cliente.dataSituacaoCadastral),
        dataSituacaoEspecialString: formatarData(cliente.dataSituacaoEspecial),
        clienteDesdeString: formatarData(cliente.clienteDesde),
      });
    }

    // eslint-disable-next-line
  }, [cliente]);

  function handleClose() {
    setAbaSelecionada('1');
    toggleModal();
  }

  // enforceFocus={false}: as janelas do SweetAlert abertas a partir daqui (ex.: reset do Workflow SOX) ficam fora
  // do modal; com a trava de foco ligada o Bootstrap roubava o foco do campo de texto a cada tecla.
  return <Modal size='xl' centered={true} show={show} onHide={handleClose} enforceFocus={false}>
    <div className="modal-content">
      <div className="modal-header" style={{ padding: '10px 15px 5px 35px' }}>
        <h5 className="modal-title titulo-modal">{title}</h5>
        <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
          <i onClick={handleClose} className="fas fa-times" style={{ color: '#5E6397' }} />
        </div>
      </div>

      <div className="modal-body" style={{ padding: '10px' }}>

        {/* Abas */}
        <div style={{ marginBottom: '10px' }}>
          {ABAS.map(aba => (
            <button
              key={aba.id}
              type="button"
              disabled={aba.id !== '1' && (cliente?.id ?? 0) === 0}
              className={`btn btn-filtro ${aba.id === abaSelecionada ? 'ativo' : ''}`}
              onClick={() => setAbaSelecionada(aba.id)}
            >
              {aba.label}
            </button>
          ))}
        </div>

        {/* Aba 1 — Dados do Cliente */}
        <div style={{ display: abaSelecionada === '1' ? 'block' : 'none' }}>
          <div className="col-md-12" style={{ display: 'flex', border: '1px solid #ddd', borderRadius: '5px', padding: '10px' }}>

            {/* Coluna Esquerda */}
            <div className="col-md-6" style={{ borderRight: '1px solid #ddd', margin: '0 10px', padding: '0 10px' }}>
              <div className="row g-3">

                <div className="col-8">
                  <label htmlFor="form-cnpj" className="form-label required">CNPJ/CPF</label>
                  <input {...formik.getFieldProps('cnpj')} placeholder="00.000.000/0000-00 ou 000.000.000-00" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.cnpj && formik.errors.cnpj },
                    { 'is-valid': formik.touched.cnpj && !formik.errors.cnpj }
                  )} id="form-cnpj" />
                </div>
                <div className="col-4">
                  <label htmlFor="form-dataAberturaString" className="form-label required">Data de Abertura</label>
                  <MaskedInput mask={MASCARA_DATA} {...formik.getFieldProps('dataAberturaString')} placeholder="00/00/0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.dataAberturaString && formik.errors.dataAberturaString },
                    { 'is-valid': formik.touched.dataAberturaString && !formik.errors.dataAberturaString }
                  )} id="form-dataAberturaString" />
                </div>

                <div className="col-12">
                  <label htmlFor="form-razaoSocial" className="form-label required">Razão Social</label>
                  <input {...formik.getFieldProps('razaoSocial')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.razaoSocial && formik.errors.razaoSocial },
                    { 'is-valid': formik.touched.razaoSocial && !formik.errors.razaoSocial }
                  )} id="form-razaoSocial" />
                </div>

                <div className="col-9">
                  <label htmlFor="form-nomeFantasia" className="form-label required">Nome Fantasia</label>
                  <input {...formik.getFieldProps('nomeFantasia')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.nomeFantasia && formik.errors.nomeFantasia },
                    { 'is-valid': formik.touched.nomeFantasia && !formik.errors.nomeFantasia }
                  )} id="form-nomeFantasia" />
                </div>
                <div className="col-3">
                  <label htmlFor="form-porte" className="form-label required">Porte</label>
                  <input {...formik.getFieldProps('porte')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.porte && formik.errors.porte },
                    { 'is-valid': formik.touched.porte && !formik.errors.porte }
                  )} id="form-porte" />
                </div>

                <div className="col-12">
                  <label htmlFor="form-atividadePrincipal" className="form-label required">Código e Descrição da Atividade Principal</label>
                  <input {...formik.getFieldProps('atividadePrincipal')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.atividadePrincipal && formik.errors.atividadePrincipal },
                    { 'is-valid': formik.touched.atividadePrincipal && !formik.errors.atividadePrincipal }
                  )} id="form-atividadePrincipal" />
                </div>

                <div className="col-12">
                  <label htmlFor="form-atividadeSecundaria" className="form-label">Código e Descrição da Atividade Secundária</label>
                  <input {...formik.getFieldProps('atividadeSecundaria')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.atividadeSecundaria && formik.errors.atividadeSecundaria },
                    { 'is-valid': formik.touched.atividadeSecundaria && !formik.errors.atividadeSecundaria }
                  )} id="form-atividadeSecundaria" />
                </div>

                <div className="col-9">
                  <label htmlFor="form-endereco" className="form-label required">Endereço</label>
                  <input {...formik.getFieldProps('endereco')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.endereco && formik.errors.endereco },
                    { 'is-valid': formik.touched.endereco && !formik.errors.endereco }
                  )} id="form-endereco" />
                </div>
                <div className="col-3">
                  <label htmlFor="form-numero" className="form-label required">Número</label>
                  <input {...formik.getFieldProps('numero')} placeholder="Nº" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.numero && formik.errors.numero },
                    { 'is-valid': formik.touched.numero && !formik.errors.numero }
                  )} id="form-numero" />
                </div>

                <div className="col-9">
                  <label htmlFor="form-complemento" className="form-label">Complemento</label>
                  <input {...formik.getFieldProps('complemento')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.complemento && formik.errors.complemento },
                    { 'is-valid': formik.touched.complemento && !formik.errors.complemento }
                  )} id="form-complemento" />
                </div>
                <div className="col-3">
                  <label htmlFor="form-cep" className="form-label required">CEP</label>
                  <MaskedInput mask={MASCARA_CEP} {...formik.getFieldProps('cep')} placeholder="00000-000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.cep && formik.errors.cep },
                    { 'is-valid': formik.touched.cep && !formik.errors.cep }
                  )} id="form-cep" />
                </div>

              </div>
            </div>

            {/* Coluna Direita */}
            <div className="col-md-6" style={{ padding: '0 10px' }}>
              <div className="row g-3">

                <div className="col-4">
                  <label htmlFor="form-bairro" className="form-label required">Bairro</label>
                  <input {...formik.getFieldProps('bairro')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.bairro && formik.errors.bairro },
                    { 'is-valid': formik.touched.bairro && !formik.errors.bairro }
                  )} id="form-bairro" />
                </div>
                <div className="col-3">
                  <label htmlFor="form-estado" className="form-label required">Estado</label>
                  <select {...formik.getFieldProps('estado')} className={clsx(
                    'form-select',
                    { 'is-invalid': formik.touched.estado && formik.errors.estado },
                    { 'is-valid': formik.touched.estado && !formik.errors.estado }
                  )} id="form-estado">
                    <option value="" hidden>UF</option>
                    {LISTA_ESTADOS.map((x, index) => <option key={index}>{x}</option>)}
                  </select>
                </div>
                <div className="col-5">
                  <label htmlFor="form-cidade" className="form-label required">Cidade</label>
                  <input {...formik.getFieldProps('cidade')} placeholder="Digite a cidade" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.cidade && formik.errors.cidade },
                    { 'is-valid': formik.touched.cidade && !formik.errors.cidade }
                  )} id="form-cidade" />
                </div>

                <div className="col-12">
                  <label htmlFor="form-email" className="form-label required">E-mail</label>
                  <input {...formik.getFieldProps('email')} placeholder="exemplo@email.com" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.email && formik.errors.email },
                    { 'is-valid': formik.touched.email && !formik.errors.email }
                  )} id="form-email" />
                </div>

                <div className="col-6">
                  <label htmlFor="form-telefone" className="form-label required">Telefone</label>
                  <MaskedInput mask={MASCARA_TELEFONE} {...formik.getFieldProps('telefone')} placeholder="(00) 00000-0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.telefone && formik.errors.telefone },
                    { 'is-valid': formik.touched.telefone && !formik.errors.telefone }
                  )} id="form-telefone" />
                </div>
                <div className="col-6">
                  <label htmlFor="form-telefoneAdicional" className="form-label">Telefone Adicional</label>
                  <MaskedInput mask={MASCARA_TELEFONE} {...formik.getFieldProps('telefoneAdicional')} placeholder="(00) 00000-0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.telefoneAdicional && formik.errors.telefoneAdicional },
                    { 'is-valid': formik.touched.telefoneAdicional && !formik.errors.telefoneAdicional }
                  )} id="form-telefoneAdicional" />
                </div>

                <div className="col-8">
                  <label htmlFor="form-situacaoCadastral" className="form-label required">Situação Cadastral</label>
                  <input {...formik.getFieldProps('situacaoCadastral')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.situacaoCadastral && formik.errors.situacaoCadastral },
                    { 'is-valid': formik.touched.situacaoCadastral && !formik.errors.situacaoCadastral }
                  )} id="form-situacaoCadastral" />
                </div>
                <div className="col-4">
                  <label htmlFor="form-dataSituacaoCadastralString" className="form-label required">Data da Situação Cad.</label>
                  <MaskedInput mask={MASCARA_DATA} {...formik.getFieldProps('dataSituacaoCadastralString')} placeholder="00/00/0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.dataSituacaoCadastralString && formik.errors.dataSituacaoCadastralString },
                    { 'is-valid': formik.touched.dataSituacaoCadastralString && !formik.errors.dataSituacaoCadastralString }
                  )} id="form-dataSituacaoCadastralString" />
                </div>

                <div className="col-12">
                  <label htmlFor="form-motivoSituacaoCadastral" className="form-label">Motivo da Situação Cadastral</label>
                  <input {...formik.getFieldProps('motivoSituacaoCadastral')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.motivoSituacaoCadastral && formik.errors.motivoSituacaoCadastral },
                    { 'is-valid': formik.touched.motivoSituacaoCadastral && !formik.errors.motivoSituacaoCadastral }
                  )} id="form-motivoSituacaoCadastral" />
                </div>

                <div className="col-8">
                  <label htmlFor="form-situacaoEspecial" className="form-label">Situação Especial</label>
                  <input {...formik.getFieldProps('situacaoEspecial')} placeholder="Digite algo" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.situacaoEspecial && formik.errors.situacaoEspecial },
                    { 'is-valid': formik.touched.situacaoEspecial && !formik.errors.situacaoEspecial }
                  )} id="form-situacaoEspecial" />
                </div>
                <div className="col-4">
                  <label htmlFor="form-dataSituacaoEspecialString" className="form-label">Data da Situação Esp.</label>
                  <MaskedInput mask={MASCARA_DATA} {...formik.getFieldProps('dataSituacaoEspecialString')} placeholder="00/00/0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.dataSituacaoEspecialString && formik.errors.dataSituacaoEspecialString },
                    { 'is-valid': formik.touched.dataSituacaoEspecialString && !formik.errors.dataSituacaoEspecialString }
                  )} id="form-dataSituacaoEspecialString" />
                </div>

                <div className="col-5">
                  <label htmlFor="form-clienteDesdeString" className="form-label required">Cliente Desde</label>
                  <MaskedInput mask={MASCARA_DATA} {...formik.getFieldProps('clienteDesdeString')} placeholder="00/00/0000" type="text" className={clsx(
                    'form-control',
                    { 'is-invalid': formik.touched.clienteDesdeString && formik.errors.clienteDesdeString },
                    { 'is-valid': formik.touched.clienteDesdeString && !formik.errors.clienteDesdeString }
                  )} id="form-clienteDesdeString" />
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Aba 2 — Planos */}
        <ClientesPlanoTab
          clienteId={cliente?.id ?? 0}
          ativo={abaSelecionada === '2'}
        />

        {/* Aba 3 — Volumes Contratados */}
        <ClientesVolumesContratadosTab
          clienteId={cliente?.id ?? 0}
          ativo={abaSelecionada === '3'}
        />

        {/* Aba 4 — Auditoria (controle de acesso por camadas) */}
        <ClientesAuditoriaTab
          clienteId={cliente?.id ?? 0}
          ativo={abaSelecionada === '4'}
        />

        {/* Aba 5 — Workflow (status de onboarding + reset) */}
        <ClientesWorkflowTab
          clienteId={cliente?.id ?? 0}
          nomeCliente={cliente?.nomeFantasia ?? `Cliente #${cliente?.id ?? 0}`}
          ativo={abaSelecionada === '5'}
        />

        {/* Aba 6 — Maestro Contábil (status de setup + reset) */}
        <ClientesMaestroTab
          clienteId={cliente?.id ?? 0}
          nomeCliente={cliente?.nomeFantasia ?? `Cliente #${cliente?.id ?? 0}`}
          ativo={abaSelecionada === '6'}
        />

      </div>
    </div>

    <div className="modal-footer" style={{ paddingTop: '10px', justifyContent: 'center' }}>
      {abaSelecionada === '1' && (
        <>
          <button disabled={formik.isSubmitting} onClick={handleClose} type="button" className="btn btn-modal-cancelar me-5"> CANCELAR </button>
          <button disabled={formik.isSubmitting || !formik.isValid} onClick={async () => await formik.submitForm()} type="button" className="btn btn-primario ms-5">
            {!formik.isSubmitting && ((cliente?.id ?? 0) === 0 ? 'ADICIONAR' : 'EDITAR')}
            {formik.isSubmitting && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Salvando...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </>
      )}
      {abaSelecionada !== '1' && (
        <button onClick={handleClose} type="button" className="btn btn-modal-cancelar"> FECHAR </button>
      )}
    </div>
  </Modal>
}

export default ClientesModal;
